import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Native UI Components - shadcn/ui yerine DOM hatası olmayan bileşenler
 * Tüm sayfalarda import edilebilir
 */

// Native Button
export const NativeButton = ({ 
  children, 
  onClick, 
  type = 'button', 
  variant = 'primary', 
  size = 'default',
  disabled, 
  className = '', 
  ...props 
}) => {
  const baseStyle = {
    borderRadius: '8px',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    transition: 'all 0.2s',
    opacity: disabled ? 0.6 : 1
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: '13px' },
    default: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' },
    icon: { padding: '8px', fontSize: '14px' }
  };

  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white' },
    secondary: { backgroundColor: '#f1f5f9', color: '#475569' },
    outline: { backgroundColor: 'transparent', color: '#374151', border: '1px solid #d1d5db' },
    ghost: { backgroundColor: 'transparent', color: '#374151' },
    danger: { backgroundColor: '#ef4444', color: 'white' },
    success: { backgroundColor: '#10b981', color: 'white' }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...sizes[size], ...variants[variant] }}
      className={className}
      {...props}
    >
      {children}
    </button>
  );
};

// Native Card
export const NativeCard = ({ children, className = '', style = {}, onClick }) => (
  <div
    onClick={onClick}
    style={{
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      backgroundColor: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      ...style
    }}
    className={className}
  >
    {children}
  </div>
);

export const NativeCardHeader = ({ children, className = '' }) => (
  <div 
    style={{ padding: '20px 20px 0' }} 
    className={className}
  >
    {children}
  </div>
);

export const NativeCardTitle = ({ children, className = '', icon: Icon }) => (
  <h3 
    style={{ 
      fontSize: '16px', 
      fontWeight: '600', 
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }} 
    className={`text-slate-900 dark:text-white ${className}`}
  >
    {Icon && <Icon style={{ width: '20px', height: '20px' }} />}
    {children}
  </h3>
);

export const NativeCardDescription = ({ children, className = '' }) => (
  <p 
    style={{ fontSize: '14px', margin: '4px 0 0' }} 
    className={`text-slate-500 dark:text-slate-400 ${className}`}
  >
    {children}
  </p>
);

export const NativeCardContent = ({ children, className = '', style = {} }) => (
  <div 
    style={{ padding: '20px', ...style }} 
    className={className}
  >
    {children}
  </div>
);

// Native Input
export const NativeInput = ({ 
  label, 
  id, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  disabled,
  required,
  error,
  className = '', 
  ...props 
}) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label 
        htmlFor={id} 
        style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}
        className="dark:text-slate-200"
      >
        {label}{required && ' *'}
      </label>
    )}
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: '8px',
        border: `1px solid ${error ? '#ef4444' : 'var(--card-border)'}`,
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box',
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'text',
        backgroundColor: disabled ? '#f3f4f6' : 'var(--card-bg)',
        color: 'var(--text-primary)'
      }}
      className={`native-input dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${className}`}
      {...props}
    />
    {error && (
      <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{error}</p>
    )}
  </div>
);

// Native Select
export const NativeSelect = ({ 
  label, 
  id, 
  value, 
  onChange, 
  options = [], 
  disabled,
  required,
  placeholder,
  className = '', 
  ...props 
}) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label 
        htmlFor={id} 
        style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}
        className="text-slate-700 dark:text-slate-200"
      >
        {label}{required && ' *'}
      </label>
    )}
    <select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid var(--card-border)',
        fontSize: '14px',
        outline: 'none',
        backgroundColor: disabled ? '#f3f4f6' : 'var(--card-bg)',
        boxSizing: 'border-box',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        color: 'var(--text-primary)'
      }}
      className={`native-select dark:bg-slate-700 dark:border-slate-600 dark:text-white ${className}`}
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// Native Badge
export const NativeBadge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: { backgroundColor: '#2563eb', color: 'white' },
    secondary: { backgroundColor: '#6b7280', color: 'white' },
    success: { backgroundColor: '#22c55e', color: 'white' },
    warning: { backgroundColor: '#f59e0b', color: 'white' },
    danger: { backgroundColor: '#ef4444', color: 'white' },
    outline: { backgroundColor: 'transparent', color: '#374151', border: '1px solid #d1d5db' },
    info: { backgroundColor: '#3b82f6', color: 'white' },
    charging: { backgroundColor: '#8b5cf6', color: 'white' }
  };

  return (
    <span 
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: '600',
        ...variants[variant]
      }}
      className={className}
    >
      {children}
    </span>
  );
};

// Native Alert
export const NativeAlert = ({ children, variant = 'default', icon: Icon, className = '' }) => {
  const variants = {
    default: { backgroundColor: '#f8fafc', borderColor: '#e2e8f0', color: '#475569' },
    info: { backgroundColor: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' },
    success: { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' },
    warning: { backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e' },
    danger: { backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' }
  };

  return (
    <div 
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '16px',
        borderRadius: '12px',
        border: `1px solid ${variants[variant].borderColor}`,
        backgroundColor: variants[variant].backgroundColor,
        color: variants[variant].color
      }}
      className={className}
    >
      {Icon && <Icon style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />}
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

// Native Modal
export const NativeModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = '450px',
  disabled = false 
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen && !disabled) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, disabled]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !disabled) {
          onClose();
        }
      }}
      data-testid="modal-backdrop"
    >
      <div
        style={{
          backgroundColor: 'var(--card-bg)',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: maxWidth,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--card-border)'
        }}
        className="dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--card-border)'
          }}
          className="dark:border-slate-700 bg-slate-50 dark:bg-slate-900"
        >
          <h3 
            style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: 'var(--text-primary)' }}
            className="text-slate-900 dark:text-white"
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            style={{
              padding: '6px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className="hover:bg-slate-200 dark:hover:bg-slate-700"
          >
            <X style={{ width: '20px', height: '20px', color: 'var(--text-secondary)' }} className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div 
          style={{ padding: '20px', overflowY: 'auto', flex: 1, color: 'var(--text-primary)' }}
          className="text-slate-900 dark:text-slate-100"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

// Native Table
export const NativeTable = ({ children, className = '' }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }} className={className}>
      {children}
    </table>
  </div>
);

export const NativeTableHeader = ({ children }) => (
  <thead>
    <tr 
      style={{ borderBottom: '1px solid #e5e7eb' }}
      className="bg-slate-50 dark:bg-slate-900 dark:border-slate-700"
    >
      {children}
    </tr>
  </thead>
);

export const NativeTableHead = ({ children, align = 'left', className = '' }) => (
  <th 
    style={{ 
      padding: '12px 16px', 
      textAlign: align, 
      fontSize: '12px', 
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}
    className={`text-slate-500 dark:text-slate-400 ${className}`}
  >
    {children}
  </th>
);

export const NativeTableBody = ({ children }) => (
  <tbody>{children}</tbody>
);

export const NativeTableRow = ({ children, onClick, className = '' }) => (
  <tr 
    onClick={onClick}
    style={{ 
      borderBottom: '1px solid #e5e7eb',
      cursor: onClick ? 'pointer' : 'default'
    }}
    className={`dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${className}`}
  >
    {children}
  </tr>
);

export const NativeTableCell = ({ children, align = 'left', className = '', style = {} }) => (
  <td 
    style={{ 
      padding: '12px 16px', 
      fontSize: '14px', 
      textAlign: align,
      color: 'var(--table-text)',
      ...style
    }}
    className={className}
  >
    {children}
  </td>
);

// Native Progress Bar
export const NativeProgress = ({ value, max = 100, color = '#2563eb', className = '' }) => (
  <div 
    style={{ 
      height: '8px', 
      backgroundColor: '#e5e7eb', 
      borderRadius: '9999px', 
      overflow: 'hidden' 
    }}
    className={className}
  >
    <div
      style={{
        height: '100%',
        width: `${Math.min((value / max) * 100, 100)}%`,
        backgroundColor: color,
        transition: 'width 0.3s ease'
      }}
    />
  </div>
);

// Skeleton Loader
export const Skeleton = ({ className = '', style = {} }) => (
  <div 
    style={{
      backgroundColor: '#e5e7eb',
      borderRadius: '8px',
      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
      ...style
    }}
    className={className}
  />
);

// Empty State
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div style={{ textAlign: 'center', padding: '48px 24px' }}>
    {Icon && (
      <div 
        style={{ 
          width: '64px', 
          height: '64px', 
          margin: '0 auto 16px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className="bg-slate-100 dark:bg-slate-800"
      >
        <Icon style={{ width: '32px', height: '32px' }} className="text-slate-400" />
      </div>
    )}
    <h3 
      style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 8px' }}
      className="text-slate-900 dark:text-white"
    >
      {title}
    </h3>
    <p 
      style={{ fontSize: '14px', margin: 0, maxWidth: '320px', marginLeft: 'auto', marginRight: 'auto' }}
      className="text-slate-500 dark:text-slate-400"
    >
      {description}
    </p>
    {action && <div style={{ marginTop: '16px' }}>{action}</div>}
  </div>
);

export default {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardDescription,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  NativeProgress,
  Skeleton,
  EmptyState
};
